import { useState, useEffect, useMemo, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import './App.css';
import './assets/sass/pagination.css';
import './assets/sass/screenLoading.css';
import LoginComp from './Pages/LoginComp';
import Dashboard from './Pages/Dashboard';
import Settings from './Pages/Settings';
import UserSubscription from './Pages/UserSubscription';
import LiveFeeds from './Pages/LiveFeeds';
import TrendingEvents from './Pages/TrendingEvents';
import Layout from './components/Layout';
import ProtectedRoute from './components/routes/ProtectedRoute';
import sound from './assets/sounds/drop-notificaiton.mp3'
import ContactUs from './Pages/ContactUs';
import { setupApiErrorHandler } from './components/services/api';
import { RiCloseCircleLine } from "react-icons/ri";

//const makeKey = (drop) => `${String(drop.eventID)}_${String(drop.updatedOn)}`;
// const makeKey = (drop) => [
//   String(drop.eventID ?? ""),
//   String(drop.updatedOn ?? ""),
//   JSON.stringify(drop.price ?? []),
//   String(drop.section ?? "")
// ].join("_");

const makeKey = (drop) => String(drop.id ?? "");

function AppContent({ unread, setPanelOpen }) {
  const navigate = useNavigate();

  useEffect(() => {
    // Setup global error handler that redirects to login on 401/403
    setupApiErrorHandler(navigate);

  }, [navigate]);

  // Apply stored theme immediately on app mount
  // useEffect(() => {
  //   const theme = localStorage.getItem('App') || 'light';
  //   document.documentElement.setAttribute('data-bs-theme', theme);
  //   document.body.setAttribute('data-bs-theme', theme);
  // }, []);

  return (
    <div style={{ height: '100vh' }}>
      <Routes>
        <Route path='/' element={<LoginComp />} />
        <Route element={<ProtectedRoute />}>
          <Route element={<Layout unread={unread} setPanelOpen={setPanelOpen} />}>
            <Route path='/livefeed' element={<LiveFeeds />} />
            <Route path='/event-news' element={<Dashboard />} />
            <Route path='/upcoming-events' element={<TrendingEvents />} />
            {/* <Route path='/subscription' element={<UserSubscription />} /> */}
            <Route path='/settings' element={<Settings />} />
            <Route path='/help' element={<ContactUs />} />
          </Route>
        </Route>
      </Routes>
    </div>
  );
}

function AppWithNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();
  const startSWCalled = useRef(false); // Guard to prevent double execution in React Strict Mode

  const audio = useMemo(() => new Audio(sound), []);
  const playSound = () => {
    audio.currentTime = 0;
    audio.play().catch(err => console.log(err));
  }

  useEffect(() => {
    if ('Notification' in window && Notification.permission !== 'granted') {
      Notification.requestPermission().then(permission => { console.log('Notificaiton Permission:', permission); });
    }
  }, [])

  // ✅ Register SW and send START message once ready
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    if (startSWCalled.current) return; // Prevent double execution
    startSWCalled.current = true;

    let controllerChangeHandler = null;
    let retryInterval = null;

    const sendStartMessage = () => {
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: "START" });
        console.log("✅ START sent to SW - checkDrops should now be running");

        // Clean up listeners/intervals once START is sent
        if (controllerChangeHandler) {
          navigator.serviceWorker.removeEventListener('controllerchange', controllerChangeHandler);
          controllerChangeHandler = null;
        }
        if (retryInterval) {
          clearInterval(retryInterval);
          retryInterval = null;
        }
        return true;
      }
      return false;
    };

    // Register service worker and wait for it to be ready
    navigator.serviceWorker
      .register("/sw.js")
      .then((registration) => {
        console.log("SW registered");

        // Wait for SW to be ready
        return navigator.serviceWorker.ready;
      })
      .then(() => {
        console.log("SW ready");

        // Try to send START immediately
        if (!sendStartMessage()) {
          console.log("⚠️ Controller not available yet, setting up listeners...");

          // Listen for controller change
          controllerChangeHandler = () => {
            console.log("Controller changed, attempting to send START...");
            sendStartMessage();
          };
          navigator.serviceWorker.addEventListener('controllerchange', controllerChangeHandler);

          // Also retry periodically (every 200ms for up to 3 seconds)
          let attempts = 0;
          const maxAttempts = 15; // 15 * 200ms = 3 seconds
          retryInterval = setInterval(() => {
            attempts++;
            if (sendStartMessage() || attempts >= maxAttempts) {
              clearInterval(retryInterval);
              retryInterval = null;
              if (attempts >= maxAttempts) {
                console.warn("⚠️ Could not send START after retries. SW may need a page reload.");
              }
            }
          }, 200);
        }
      })
      .catch((error) => {
        console.error("Error registering/service worker:", error);
      });

    // Cleanup function
    return () => {
      if (controllerChangeHandler) {
        navigator.serviceWorker.removeEventListener('controllerchange', controllerChangeHandler);
      }
      if (retryInterval) {
        clearInterval(retryInterval);
      }
    };
  }, []);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    let timeoutId;

    const pingSW = () => {
      if (!navigator.serviceWorker.controller) return;

      navigator.serviceWorker.controller.postMessage("PING");

      timeoutId = setTimeout(() => {
        console.warn("⚠️ No PONG (SW may be sleeping — normal)");
      }, 3000);

    };

    const handleMessage = (event) => {
      if (event.data === "PONG") {
        console.log("✅ SW Alive");
        clearTimeout(timeoutId);
      }
    };

    navigator.serviceWorker.addEventListener("message", handleMessage);

    const interval = setInterval(pingSW, 15000);

    return () => {
      clearInterval(interval);
      navigator.serviceWorker.removeEventListener("message", handleMessage);
    };
  }, []);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const keepAliveInterval = setInterval(() => {
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'KEEP_ALIVE' });
      }
    }, 20000);

    return () => {
      clearInterval(keepAliveInterval);
    };
  }, []);


  // ✅ Listen messages from Service Worker
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    const handler = (event) => {
      console.log("📩 SW Message:", event.data);
      if (event.data?.type !== "NEW_DROPS") return;

      const incoming = event.data?.data || [];
      if (!Array.isArray(incoming) || incoming.length === 0) return;

      setNotifications((prev) => {
        const existingKeys = new Set(prev.map(makeKey));
        const fresh = incoming.filter((drop) => {
          return !existingKeys.has(makeKey(drop))
        });

        if (fresh.length > 0) {
          setUnread((u) => u + fresh.length);
          setPanelOpen(true);
          playSound();
          return [...fresh, ...prev];
        }
        return prev;
      });
    };
    navigator.serviceWorker.addEventListener("message", handler);

    // return () => {
    //   navigator.serviceWorker.removeEventListener("message", handler);
    // };
  }, [audio]);

  const ConvertServerTime = (serverTime) => {
    if (!serverTime) return "";
    const actualDate = serverTime.replace(/\//g, "-");
    console.log(actualDate);
    const isoString = new Date(actualDate.replace(" ", "T") + "Z");
    const dateTimeStr = isoString.toLocaleString();
    return dateTimeStr;
    // const [date, time] = serverTime.split(" ");
    // if (!date || !time) return serverTime;

    // const [year, month, day] = date.split("/").map(Number);
    // const [hour, minute, second] = time.split(":").map(Number);

    // const dateUTC = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
    // return dateUTC.toLocaleString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true, });
  }

  return (
    <>
      {panelOpen && (
        <div
          style={{
            position: "fixed",
            top: "65px",
            right: "15px",
            width: "360px",
            maxHeight: "75vh",
            overflowY: "auto",
            zIndex: 9999,
            backdropFilter: "blur(12px)",
            borderRadius: "14px",
            padding: "10px 10px 0px 10px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.15)"
          }}
        >
          {notifications.length === 0 && (
            <p className="text-center text-muted">No notifications</p>
          )}

          {(notifications).map((drop) => (
            <div
              key={makeKey(drop)}

              style={{
                padding: "15px",
                marginBottom: "10px",
                borderRadius: "10px",
                background:
                  "linear-gradient(325deg, rgba(109, 99, 241, 0.29), rgba(255, 255, 255, 0.09))",
              }}
            >
              <div className='d-flex justify-content-between align-item-start'>
                <strong>{drop.eventName}</strong>
                <span onClick={() => {
                  setNotifications(prev => prev.filter(d => makeKey(d) !== makeKey(drop)));
                  setUnread(u => Math.max(u - 1, 0));
                  setPanelOpen(false);
                }}>
                  <RiCloseCircleLine />

                </span>
              </div>
              <div onClick={() => {
                navigate('/livefeed')
              }} style={{cursor: 'pointer'}}>
                <div style={{ fontSize: "14px" }} >
                  <p>{drop.eventVenue}</p>
                  <p>{drop.eventDate}</p>
                  <p>Section: {drop.section} | Row: {drop.row}</p>
                </div>
                <div className="text-end mt-2" style={{ fontSize: '13px', fontWeight: '500' }}>
                  Release On: {ConvertServerTime(drop.updatedOn)}
                </div>
              </div>
            </div>

          ))}
        </div>
      )}
      <AppContent unread={unread} setPanelOpen={setPanelOpen} />
    </>
  );
}

export default function App() {
  return (
    <Router>
      <AppWithNotifications />
    </Router>
  );
}

